import { useRef } from "react";
import { AppDispatch, RootState } from "../app/store";
import { Onboarding, OnboardingType } from "../types";
import { useNavigate } from "react-router-dom";
import {
  onUpdateActiveTemplate,
  onUpdateOnboardingType,
} from "../reducers/activeTemplateReducer";
import { FormikValues } from "formik";
import {
  onUpdateStep,
  onUpdateSubStep,
} from "../reducers/onboardingDataReducer";
import {
  onUpdateStep as onUpdateSecuritiesStep,
  onUpdateSubStep as onUpdateSecuritiesSubStep,
  onUpdateHolder,
} from "../reducers/securitiesReducer";
import onboardingSecurities from "../templates/allaria/securities";
import onboardingSignatory from "../templates/allaria/signatory";
import onboardingJuridical from "../templates/allaria/juridical";
import onboardingPhysical from "../templates/allaria/physical";
import onboardingCoowner from "../templates/allaria/coowner";
import onboardingAdditionalCoowner from "../templates/allaria/additionalCoowner";
import onboardingCrypto from "../templates/allaria/crypto";
import onboardingSav3 from "../templates/partnerships/sav3";
import { getPartnership } from "../utils/companies";
import { showOnlyInDev } from "../constants";

const PARTNERSHIP_TEMPLATES: Record<string, Onboarding> = {
  sav3: onboardingSav3,
};

const useTemplateInterpreter = ({
  dispatch,
  onboardingType,
  state,
  values,
  draftUUID: uuid,
}: {
  dispatch: AppDispatch;
  onboardingType: OnboardingType;
  state: RootState;
  values?: FormikValues;
  draftUUID?: string;
}) => {
  const navigate = useNavigate();

  const {
    onboardingData: {
      stepData: { step, subStep },
    },
  } = state;

  const { draftUUID, formValues } =
    onboardingType === "SECURITIES" ? state.securities : state.onboardingData;

  const fetchTemplate = async () => {
    if (!onboardingType) {
      window.location.href = "/";
      return;
    }

    const partnership = getPartnership(values);

    let template;

    switch (onboardingType) {
      case "PHYSICAL": {
        template =
          showOnlyInDev && partnership && PARTNERSHIP_TEMPLATES[partnership.key]
            ? await PARTNERSHIP_TEMPLATES[partnership.key]?.(
                uuid || draftUUID,
                dispatch,
                formValues,
                navigate,
              )
            : await onboardingPhysical(uuid || draftUUID, dispatch, formValues);
        break;
      }
      case "COOWNER":
        template = await onboardingCoowner(
          dispatch,
          uuid || draftUUID,
          formValues,
        );
        break;
      case "ADDITIONAL_COOWNER":
        template = await onboardingAdditionalCoowner(
          dispatch,
          uuid || draftUUID,
          formValues,
        );
        break;
      case "JURIDICAL":
        template = await onboardingJuridical(uuid || draftUUID, dispatch);
        break;
      case "SIGNATORY":
        template = await onboardingSignatory(dispatch);
        break;
      case "SECURITIES":
        template = await onboardingSecurities({
          values: values ?? (formValues || {}),
          state,
          dispatch,
        });
        break;
      case "DIGITAL":
        template = await onboardingCrypto(dispatch);
        break;
      default:
        break;
    }

    dispatch(onUpdateActiveTemplate(template));
  };

  const inFlightTemplateRef = useRef<Promise<void> | null>(null);

  // inFlightTemplateRef  prevents refetching
  const loadTemplate = () => {
    if (inFlightTemplateRef.current) return inFlightTemplateRef.current;

    const request = fetchTemplate().finally(() => {
      inFlightTemplateRef.current = null;
    });
    inFlightTemplateRef.current = request;

    return request;
  };

  const redirectToOnboarding = () => {
    if (window.location.pathname === "/personal") {
      dispatch(onUpdateOnboardingType("PHYSICAL"));
      dispatch(onUpdateStep(step || 1));
      dispatch(onUpdateSubStep(subStep || 1));
    }

    if (window.location.pathname === "/empresarial") {
      dispatch(onUpdateOnboardingType("JURIDICAL"));
      dispatch(onUpdateStep(1));
      dispatch(onUpdateSubStep(1));
    }

    if (window.location.pathname === "/firmante") {
      dispatch(onUpdateOnboardingType("SIGNATORY"));
      dispatch(onUpdateStep(1));
      dispatch(onUpdateSubStep(1));
    }

    if (window.location.pathname === "/cotitularidad") {
      dispatch(onUpdateOnboardingType("COOWNER"));
      dispatch(onUpdateStep(1));
      dispatch(onUpdateSubStep(1));
    }

    if (window.location.pathname === "/cotitular-adicional") {
      dispatch(onUpdateOnboardingType("ADDITIONAL_COOWNER"));
      dispatch(onUpdateStep(1));
      dispatch(onUpdateSubStep(1));
    }

    if (window.location.pathname === "/securities") {
      dispatch(onUpdateOnboardingType("SECURITIES"));
      dispatch(onUpdateHolder(1));
      dispatch(onUpdateSecuritiesStep("0"));
      dispatch(onUpdateSecuritiesSubStep(1));
    }

    if (window.location.pathname === "/digital") {
      dispatch(onUpdateOnboardingType("DIGITAL"));
      dispatch(onUpdateStep(step || 1));
      dispatch(onUpdateSubStep(subStep || 1));
    }
  };

  const urlTemplate = getUrlTemplate({
    onboardingType,
    uuid: uuid || draftUUID,
    email: formValues?.email,
  });

  return {
    formValues,
    loadTemplate,
    urlTemplate,
    draftUUID,
    redirectToOnboarding,
  };
};

export const getUrlTemplate = ({
  onboardingType,
  uuid,
  email,
}: {
  onboardingType: OnboardingType;
  uuid?: string;
  email?: string;
}) => {
  switch (onboardingType) {
    case "COOWNER":
      return "/cotitularidad";
    case "ADDITIONAL_COOWNER":
      return `/cotitular-adicional?externalCode=${uuid}&email=${email}`;
    case "PHYSICAL":
      return "/personal";
    case "JURIDICAL":
      return "/empresarial";
    case "SIGNATORY":
      return `/firmante?externalCode=${uuid}&email=${email}`;
    case "SECURITIES":
      return "/securities";
    case "DIGITAL":
      return "/digital";
    default:
      return "/";
  }
};

export default useTemplateInterpreter;
